import React, { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { server } from "../server";
import departmentData from "../jsons/departments.json";
import { Building2, Globe, Network, Plus, UploadCloud, FileText, Hash } from "lucide-react";

export default function AddDepartment() {
  const [formData, setFormData] = useState({
    departmentName: "",
    departmentSlug: "",
    iPv4Address: "",
    vlanId: "",
  });

  const [bulkLoading, setBulkLoading] = useState(false);
  const [singleLoading, setSingleLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const newData = { ...prev, [name]: value };
      if (name === "departmentName") {
        newData.departmentSlug = value.toLowerCase().replace(/\s+/g, '-');
      }
      return newData;
    });
  };

  const handleSingleSubmit = async (e) => {
    e.preventDefault();
    setSingleLoading(true);
    try {
      const payload = {
        ...formData,
        vlanId: Number(formData.vlanId),
      };

      const token = localStorage.getItem("dbToken");
      await axios.post(`${server}/department/Create`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      toast.success("Department added successfully!");
      setFormData({
        departmentName: "",
        departmentSlug: "",
        iPv4Address: "",
        vlanId: "",
      });
    } catch (error) {
      console.error("Error adding department:", error);
      toast.error(error.response?.data?.message || "Failed to add department");
    } finally {
      setSingleLoading(false);
    }
  };

  const handleBulkUpload = async () => {
    if (
      !window.confirm("Are you sure you want to upload all departments from the JSON?")
    )
      return;

    setBulkLoading(true);
    const departments = departmentData["VLAN ID"] || [];
    let successCount = 0;
    let failedCount = 0;
    const token = localStorage.getItem("dbToken");

    for (const dept of departments) {
      try {
        const payload = {
          departmentName: dept.Department,
          departmentSlug: dept.Department.toLowerCase().replace(/\s+/g, "-"),
          iPv4Address: dept.IPv4,
          vlanId: Number(dept["VLAN ID"]),
        };

        await axios.post(`${server}/department/Create`, payload, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        successCount++;
      } catch (error) {
        console.error(`Failed to upload ${dept.Department}:`, error);
        failedCount++;
      }
    }

    toast.info(
      `Bulk upload complete. Success: ${successCount}, Failed: ${failedCount}`
    );
    setBulkLoading(false);
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
              <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Add Department</h1>
              <p className="text-sm text-gray-500 mt-1">Create new departments and configure network settings.</p>
          </div>
          
          <button
            onClick={handleBulkUpload}
            disabled={bulkLoading}
            className={`hidden items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all border ${
              bulkLoading 
                ? "bg-gray-50 text-gray-400 border-gray-100 cursor-not-allowed" 
                : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300"
            }`}
             title="This feature is optional"
          >
             {bulkLoading ? <div className="animate-spin h-4 w-4 border-2 border-gray-300 border-t-gray-600 rounded-full" /> : <UploadCloud className="h-4 w-4" />}
            {bulkLoading ? "Uploading..." : "Bulk Upload"}
          </button>
      </div>

      {/* Main Form Card */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 md:p-8 max-w-4xl">
        <div className="flex items-center gap-3 mb-8 pb-6 border-b border-gray-100">
             <div className="h-10 w-10 bg-blue-50 rounded-full flex items-center justify-center">
                 <Building2 className="h-5 w-5 text-blue-600" />
             </div>
             <div>
                 <h2 className="text-lg font-bold text-gray-900">Department Details</h2>
                 <p className="text-xs text-gray-500">Please enter the details below</p>
             </div>
        </div>

        <form onSubmit={handleSingleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Department Name */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">Department Name</label>
                <div className="relative group">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 group-hover:text-blue-500 transition-colors" />
                    <input
                      type="text"
                      name="departmentName"
                      value={formData.departmentName}
                      onChange={handleChange}
                      placeholder="e.g. Finance"
                      className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all placeholder-gray-400"
                      required
                    />
                </div>
              </div>

              {/* Department Slug */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">Department Slug</label>
                <div className="relative group">
                    <FileText className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 group-hover:text-blue-500 transition-colors" />
                    <input
                      type="text"
                      name="departmentSlug"
                      value={formData.departmentSlug}
                      onChange={handleChange}
                      placeholder="auto-generated-slug"
                      className="w-full pl-9 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all placeholder-gray-400"
                      required
                      readOnly
                    />
                </div>
              </div>

              {/* IPv4 Address */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">IPv4 Address</label>
                <div className="relative group">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 group-hover:text-blue-500 transition-colors" />
                    <input
                      type="text"
                      name="iPv4Address"
                      value={formData.iPv4Address}
                      onChange={handleChange}
                      placeholder="e.g. 10.10.100.0/24"
                      className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all placeholder-gray-400"
                      required
                    />
                </div>
              </div>

              {/* VLAN ID */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">VLAN ID</label>
                <div className="relative group">
                    <Network className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 group-hover:text-blue-500 transition-colors" />
                    <input
                      type="number"
                      name="vlanId"
                      value={formData.vlanId}
                      onChange={handleChange}
                      placeholder="e.g. 100"
                      className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all placeholder-gray-400"
                      required
                    />
                </div>
              </div>
          </div>

          <div className="pt-6 border-t border-gray-100">
            <button
              type="submit"
              disabled={singleLoading}
              className={`flex items-center justify-center mx-auto gap-2 w-full md:w-auto md:min-w-[200px] px-6 py-2.5 rounded-lg font-medium text-white shadow-sm transition-all ${
                singleLoading
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed shadow-none"
                  : "bg-blue-600 hover:bg-blue-700 active:bg-blue-800 hover:shadow"
              }`}
            >
              {singleLoading ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white/30 border-t-white rounded-full" />
                    <span>Adding Department...</span>
                  </>
              ) : (
                  <>
                    <Plus className="h-4 w-4" />
                    <span>Add Department</span>
                  </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
